import { useCallback, useState } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useSearchActions } from '#src/app/pages/search/shared/hooks';
import { useCurrentUser, useSession } from '#src/app/shared/hooks/session';

import { useResolveCityState } from './hooks/useResolveCityState';

type Result = ModelHookResult<
	{ isNearYouLoading: boolean },
	{
		filterByNearYou: () => void;
	}
>;

export function useFilterByNearYou(): Result {
	const user = useCurrentUser();
	const sessionCountryCode = useSession().country.countryCode;
	const track = useTracking();
	const actions = useSearchActions();

	const {
		actions: { resolveCityState },
	} = useResolveCityState();

	const [isNearYouLoading, setIsNearYouLoading] = useState(false);

	const filterByCityResolved = useCallback(
		async (city: string, countryCode: string, stateCode?: string) => {
			setIsNearYouLoading(true);
			const cityState = await resolveCityState({ city, countryCode, stateCode });
			actions.setLocationCity(cityState);
			setIsNearYouLoading(false);
		},
		[actions, resolveCityState],
	);

	return {
		data: { isNearYouLoading },
		actions: {
			filterByNearYou: useCallback(() => {
				if (isNearYouLoading) return;

				void track('search', { q: '', context: 'landing_near_me_button' });

				const city = user?.city;
				const stateCode = user?.state?.stateCode ? user.state.stateCode : undefined;
				const countryCode = user?.country?.countryCode || sessionCountryCode;

				if (city) {
					void filterByCityResolved(city, countryCode, stateCode);
					return;
				}
				actions.setLocationCountry(countryCode);
			}, [filterByCityResolved, actions, isNearYouLoading, track, user, sessionCountryCode]),
		},
	};
}
